// Overspeed Event Map Playback Modal
// ---------------------------------------------------------------------------
// Given the raw AssetPro iot-device-data rows that fed the recompute and a
// single detector event, this component renders a Leaflet map that:
//   - draws the full pre/during/post polyline for the window
//     [event.start - 2min, event.end + 2min]
//   - highlights the segment that actually constitutes the event (red)
//   - animates a vehicle marker along the timestamps, so the reviewer can
//     watch the run and visually sanity-check whether the event is real
//   - exposes play / pause / speed / seek controls
//
// Parsing of `lat_long` and speed uses the same conventions as
// utilizationEngine.js (row.lat_long = "lat,lng"; speed_gnss kmph).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, Row, Col, Button, Slider, Typography, Tag, Icon, Select,
} from 'antd';
import moment from 'moment';
import L from 'leaflet';
import {
  Map as LeafletMap, TileLayer, Marker, Polyline, CircleMarker, Tooltip,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const { Text } = Typography;
const { Option } = Select;

// CRA/webpack strips Leaflet's default marker asset paths. Rebind them
// against the CDN so the blue marker shows up without extra asset wiring.
// Safe to do once per module load.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const PRE_POST_MS = 2 * 60 * 1000; // 2 minutes before and after the event

const parseLatLng = (str) => {
  if (!str || typeof str !== 'string') return null;
  const parts = str.split(',').map((s) => Number(String(s).trim()));
  if (parts.length !== 2) return null;
  const [lat, lng] = parts;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return [lat, lng];
};

// Map a raw AssetPro iot-device-data row into a lightweight point.
const toPoint = (row) => {
  const ll = parseLatLng(row.lat_long) || (
    row.lat != null && row.lon != null ? [Number(row.lat), Number(row.lon)] : null
  );
  if (!ll) return null;
  const ts = Number(row.timestamp);
  if (!Number.isFinite(ts)) return null;
  return {
    ts,
    lat: ll[0],
    lng: ll[1],
    speed: Number.isFinite(Number(row.speed_gnss)) ? Number(row.speed_gnss) : null,
    datetimeIst: row.datetime_ist,
    hdop: Number.isFinite(Number(row.gnss_hdop)) ? Number(row.gnss_hdop) : null,
  };
};

const fmtTs = (ms) => (Number.isFinite(ms) ? moment(ms).format('HH:mm:ss') : '-');
const fmtTsFull = (ms) => (Number.isFinite(ms) ? moment(ms).format('DD MMM YYYY HH:mm:ss') : '-');

// ---------------------------------------------------------------------------

const OverspeedEventMapModal = ({ visible, onClose, rows, event, speedLimitKmph }) => {
  const mapRef = useRef(null);
  const tickTimerRef = useRef(null);
  const playbackSpeedRef = useRef(4);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(4);  // multiplier relative to wall clock
  const [cursor, setCursor] = useState(0);                // index into trackPoints

  // Build the track points & windowing -------------------------------------
  const { windowPoints, eventStart, eventEnd, windowStart, windowEnd } = useMemo(() => {
    if (!event || !Array.isArray(rows) || rows.length === 0) {
      return { windowPoints: [], eventStart: null, eventEnd: null, windowStart: null, windowEnd: null };
    }
    const eStart = Number(event.start_ts_ms);
    const eEnd   = Number(event.end_ts_ms);
    if (!Number.isFinite(eStart) || !Number.isFinite(eEnd)) {
      return { windowPoints: [], eventStart: null, eventEnd: null, windowStart: null, windowEnd: null };
    }
    const wStart = eStart - PRE_POST_MS;
    const wEnd   = eEnd   + PRE_POST_MS;
    const points = rows
      .map(toPoint)
      .filter((p) => p && p.ts >= wStart && p.ts <= wEnd)
      .sort((a, b) => a.ts - b.ts);

    return {
      windowPoints: points,
      eventStart:  eStart,
      eventEnd:    eEnd,
      windowStart: wStart,
      windowEnd:   wEnd,
    };
  }, [rows, event]);

  // Split polyline into three segments for coloring ------------------------
  const { preSeg, duringSeg, postSeg } = useMemo(() => {
    const pre = [], dur = [], post = [];
    for (const p of windowPoints) {
      if (p.ts < eventStart) pre.push([p.lat, p.lng]);
      else if (p.ts <= eventEnd) dur.push([p.lat, p.lng]);
      else post.push([p.lat, p.lng]);
    }
    // Ensure segments visually connect by sharing the boundary points.
    if (pre.length && dur.length) pre.push(dur[0]);
    if (dur.length && post.length) dur.push(post[0]);
    return { preSeg: pre, duringSeg: dur, postSeg: post };
  }, [windowPoints, eventStart, eventEnd]);

  // Reset cursor and playback when event changes ---------------------------
  useEffect(() => {
    if (!visible) return;
    // Start a bit before the event itself so the "pre" context is visible.
    const startIdx = Math.max(0, windowPoints.findIndex((p) => p.ts >= eventStart) - 5);
    setCursor(Math.max(0, startIdx));
    setIsPlaying(true);
  }, [visible, windowPoints, eventStart]);

  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
  }, [playbackSpeed]);

  // Playback loop ----------------------------------------------------------
  useEffect(() => {
    if (!visible || !isPlaying || windowPoints.length < 2) return undefined;
    // Advance ~8 points per second at 1× — gives a smooth feel for 1 Hz data
    // when sped up 4-8×. Pace derives from the timestamp delta between the
    // current point and the next to preserve proportional timing.
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setCursor((idx) => {
        if (idx >= windowPoints.length - 1) {
          setIsPlaying(false);
          return idx;
        }
        return idx + 1;
      });
    };
    const schedule = () => {
      const idx = cursor;
      if (idx >= windowPoints.length - 1) { setIsPlaying(false); return; }
      const dt = Math.max(100, (windowPoints[idx + 1].ts - windowPoints[idx].ts));
      const multiplier = Math.max(0.25, Number(playbackSpeedRef.current) || 1);
      const delay = Math.max(20, Math.min(1500, dt / multiplier));
      tickTimerRef.current = setTimeout(() => {
        tick();
        schedule();
      }, delay);
    };
    schedule();
    return () => {
      cancelled = true;
      if (tickTimerRef.current) clearTimeout(tickTimerRef.current);
    };
    // `cursor` is intentionally in the dep list so the schedule re-aligns
    // after seek/step operations.
  }, [visible, isPlaying, windowPoints, cursor]);

  // Fit bounds on open ------------------------------------------------------
  useEffect(() => {
    if (!visible) return;
    if (!mapRef.current || windowPoints.length === 0) return;
    const map = mapRef.current.leafletElement || mapRef.current;
    if (!map) return;
    const bounds = L.latLngBounds(windowPoints.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [32, 32], maxZoom: 18, animate: false });
  }, [visible, windowPoints]);

  // Derived state for the controls -----------------------------------------
  const current = windowPoints[cursor] || null;
  const peakPoint = event && Number.isFinite(event.peak_lat) && Number.isFinite(event.peak_lon)
    ? [Number(event.peak_lat), Number(event.peak_lon)]
    : null;
  const center = current ? [current.lat, current.lng]
    : (peakPoint || (windowPoints[0] ? [windowPoints[0].lat, windowPoints[0].lng] : [20, 77]));

  const handleSeek = (pct) => {
    if (windowPoints.length === 0) return;
    const idx = Math.max(0, Math.min(windowPoints.length - 1, Math.round((pct / 100) * (windowPoints.length - 1))));
    setCursor(idx);
  };

  const cursorPct = windowPoints.length > 1
    ? Math.round((cursor / (windowPoints.length - 1)) * 100)
    : 0;

  const speedColor = (v) => {
    if (v == null || !Number.isFinite(v)) return 'default';
    if (v >= (speedLimitKmph || 30)) return 'red';
    if (v >= (speedLimitKmph || 30) * 0.8) return 'orange';
    return 'green';
  };

  return (
    <Modal
      visible={visible}
      title={
        <span>
          <Icon type="environment" style={{ marginRight: 8 }} />
          Event Replay — {event ? `${fmtTsFull(eventStart)} → ${fmtTsFull(eventEnd)}` : ''}
        </span>
      }
      onCancel={onClose}
      footer={null}
      width={980}
      bodyStyle={{ padding: 12 }}
      destroyOnClose
    >
      {/* Metadata strip */}
      <Row gutter={8} style={{ marginBottom: 8 }}>
        <Col><Tag color="blue">Peak {event?.peak_speed_kmph != null ? Number(event.peak_speed_kmph).toFixed(1) : '-'} kmph</Tag></Col>
        <Col><Tag>Limit {speedLimitKmph ?? '-'} kmph</Tag></Col>
        <Col><Tag>Duration {event?.duration_sec != null ? Number(event.duration_sec).toFixed(1) : '-'} s</Tag></Col>
        <Col><Tag color="geekblue">{windowPoints.length} raw samples in window</Tag></Col>
        {current && (
          <Col style={{ marginLeft: 'auto' }}>
            <Tag color={speedColor(current.speed)}>
              t={fmtTs(current.ts)}  speed={current.speed != null ? Number(current.speed).toFixed(1) : '-'} kmph
            </Tag>
          </Col>
        )}
      </Row>

      {windowPoints.length < 2 ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Text type="secondary">
            Not enough GPS points in the window to render a map. The recompute may have used a
            very sparse sample; try a longer event or check raw telemetry availability for this
            vehicle.
          </Text>
        </div>
      ) : (
        <>
          <div style={{ height: 480, width: '100%' }}>
            <LeafletMap
              ref={mapRef}
              style={{ height: '100%', width: '100%' }}
              center={center}
              zoom={16}
              scrollWheelZoom
            >
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {preSeg.length >= 2 && (
                <Polyline positions={preSeg} color="#8c8c8c" weight={3} opacity={0.7} />
              )}
              {duringSeg.length >= 2 && (
                <Polyline positions={duringSeg} color="#f5222d" weight={5} opacity={0.9} />
              )}
              {postSeg.length >= 2 && (
                <Polyline positions={postSeg} color="#8c8c8c" weight={3} opacity={0.7} />
              )}

              {/* Dot markers for every sample — small so they don't clutter. */}
              {windowPoints.map((p, i) => {
                const inEvent = p.ts >= eventStart && p.ts <= eventEnd;
                return (
                  <CircleMarker
                    key={`s-${i}`}
                    center={[p.lat, p.lng]}
                    radius={inEvent ? 3 : 2}
                    color={inEvent ? '#f5222d' : '#8c8c8c'}
                    fillOpacity={0.8}
                    stroke={false}
                  >
                    <Tooltip>
                      <div>
                        {fmtTs(p.ts)} — {p.speed != null ? `${Number(p.speed).toFixed(1)} kmph` : '-'}
                        {p.hdop != null ? <div>HDOP: {p.hdop.toFixed(2)}</div> : null}
                      </div>
                    </Tooltip>
                  </CircleMarker>
                );
              })}

              {/* Peak marker */}
              {peakPoint && (
                <CircleMarker
                  center={peakPoint}
                  radius={8}
                  color="#722ed1"
                  fillOpacity={0.9}
                  stroke
                  weight={2}
                >
                  <Tooltip permanent direction="top" offset={[0, -8]}>
                    Peak {event?.peak_speed_kmph != null ? Number(event.peak_speed_kmph).toFixed(1) : '-'} kmph
                  </Tooltip>
                </CircleMarker>
              )}

              {/* Animated vehicle marker */}
              {current && (
                <Marker position={[current.lat, current.lng]}>
                  <Tooltip permanent direction="top" offset={[0, -32]}>
                    {current.speed != null ? `${Number(current.speed).toFixed(1)} kmph` : '-'}
                  </Tooltip>
                </Marker>
              )}
            </LeafletMap>
          </div>

          {/* Controls */}
          <Row gutter={12} style={{ marginTop: 12 }} align="middle">
            <Col>
              <Button
                type="primary"
                icon={isPlaying ? 'pause' : 'caret-right'}
                onClick={() => setIsPlaying((p) => !p)}
              >
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
            </Col>
            <Col>
              <Button
                icon="step-backward"
                onClick={() => setCursor((i) => Math.max(0, i - 1))}
              />
            </Col>
            <Col>
              <Button
                icon="step-forward"
                onClick={() => setCursor((i) => Math.min(windowPoints.length - 1, i + 1))}
              />
            </Col>
            <Col>
              <Select
                value={playbackSpeed}
                onChange={setPlaybackSpeed}
                style={{ width: 90 }}
              >
                <Option value={1}>1×</Option>
                <Option value={2}>2×</Option>
                <Option value={4}>4×</Option>
                <Option value={8}>8×</Option>
                <Option value={16}>16×</Option>
              </Select>
            </Col>
            <Col flex="auto">
              <Slider
                min={0}
                max={100}
                value={cursorPct}
                tipFormatter={(v) => {
                  const idx = Math.round((v / 100) * (windowPoints.length - 1));
                  return fmtTs(windowPoints[idx]?.ts);
                }}
                onChange={handleSeek}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 4 }}>
            <Col>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Window: {fmtTs(windowStart)} – {fmtTs(windowEnd)} (2 min pre + event + 2 min post).
                Red = during event, grey = pre/post context, purple = peak.
              </Text>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );
};

export default OverspeedEventMapModal;
